import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/utils/i18n";
import type { CertificateTemplate, TextPosition } from "../../config/certificate-templates";
import type { CertificateData } from "../../utils/certificate-pdf-generator";
import { formatCertificateDate } from "../../utils/certificate-pdf-generator";
import styles from "./CertificatePositionEditor.module.scss";

type FieldKey = keyof CertificateTemplate["textPositions"];

const FIELD_LIST: { key: FieldKey; label: string }[] = [
  { key: "educationTitle", label: i18n.t("certificatePositionEditor.fields.educationTitle") },
  { key: "participantName", label: i18n.t("certificatePositionEditor.fields.participantName") },
  { key: "description", label: i18n.t("certificatePositionEditor.fields.description") },
  { key: "instructorName", label: i18n.t("certificatePositionEditor.fields.instructorName") },
  { key: "issueDate", label: "Tarih" },
  { key: "certificateNumber", label: "Sertifika No" },
];

const FONT_FAMILIES = [
  { value: "'Times New Roman', Georgia, serif", label: "Times New Roman" },
  { value: "Georgia, serif", label: "Georgia" },
  { value: "Arial, sans-serif", label: "Arial" },
  { value: "'Helvetica Neue', Helvetica, sans-serif", label: "Helvetica" },
  { value: "'Courier New', monospace", label: "Courier New" },
];

const FONT_WEIGHTS = ["300", "400", "500", "600", "700", "bold"];

// A4 landscape @ 96 DPI, matching CertificatePreview
const CANVAS_W = 842;
const CANVAS_H = 595;

interface Props {
  template: CertificateTemplate;
  positions: CertificateTemplate["textPositions"];
  data: CertificateData;
  onChange: (positions: CertificateTemplate["textPositions"]) => void;
  onReset: () => void;
}

const CertificatePositionEditor: React.FC<Props> = ({
  template,
  positions,
  data,
  onChange,
  onReset,
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<FieldKey>("participantName");
  const [dragging, setDragging] = useState<FieldKey | null>(null);
  const dragOffsetRef = useRef<{ dx: number; dy: number } | null>(null);

  const formattedDate = useMemo(() => {
    if (!data.issueDate) return "";
    try {
      return formatCertificateDate(data.issueDate);
    } catch {
      return data.issueDate;
    }
  }, [data.issueDate]);

  const fieldValues: Record<FieldKey, { value: string; labelPrefix?: string }> = {
    educationTitle: { value: data.educationTitle, labelPrefix: i18n.t("certificatePositionEditor.fields.educationTitle") },
    participantName: { value: data.participantName },
    description: { value: data.description },
    instructorName: { value: data.instructorName, labelPrefix: i18n.t("certificatePositionEditor.fields.instructorName") },
    issueDate: { value: formattedDate, labelPrefix: "Tarih" },
    certificateNumber: { value: data.certificateNumber, labelPrefix: "Sertifika No" },
  };

  const updatePos = (key: FieldKey, patch: Partial<TextPosition>) => {
    onChange({ ...positions, [key]: { ...positions[key], ...patch } });
  };

  const handleMouseDown = (key: FieldKey, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSelected(key);
    setDragging(key);
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const pos = positions[key];
    const elX = (pos.x / 100) * rect.width;
    const elY = (pos.y / 100) * rect.height;
    dragOffsetRef.current = {
      dx: e.clientX - (rect.left + elX),
      dy: e.clientY - (rect.top + elY),
    };
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragging || !canvasRef.current || !dragOffsetRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const px = e.clientX - rect.left - dragOffsetRef.current.dx;
      const py = e.clientY - rect.top - dragOffsetRef.current.dy;
      const x = Math.min(100, Math.max(0, (px / rect.width) * 100));
      const y = Math.min(100, Math.max(0, (py / rect.height) * 100));
      updatePos(dragging, { x: Number(x.toFixed(2)), y: Number(y.toFixed(2)) });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dragging, positions],
  );

  const handleMouseUp = useCallback(() => {
    setDragging(null);
    dragOffsetRef.current = null;
  }, []);

  useEffect(() => {
    if (!dragging) return;
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging, handleMouseMove, handleMouseUp]);

  const current = positions[selected];

  return (
    <div className={styles.editor}>
      <div className={styles.canvasWrap}>
        <div
          ref={canvasRef}
          className={styles.canvas}
          style={{ width: CANVAS_W, height: CANVAS_H }}
        >
          <img
            src={template.imagePath}
            alt={template.name}
            className={styles.bgImage}
            crossOrigin="anonymous"
            draggable={false}
          />
          {FIELD_LIST.map(({ key, label }) => {
            const pos = positions[key];
            const fv = fieldValues[key];
            const active = selected === key;
            const display = fv.value || `[${label}]`;
            const translateX =
              pos.textAlign === "center"
                ? "-50%"
                : pos.textAlign === "right"
                  ? "-100%"
                  : "0";
            return (
              <div
                key={key}
                className={`${styles.overlay} ${active ? styles.overlayActive : ""} ${
                  dragging === key ? styles.overlayDragging : ""
                }`}
                style={{
                  left: `${pos.x}%`,
                  top: `${pos.y}%`,
                  transform: `translateX(${translateX})`,
                  fontFamily: pos.fontFamily,
                  fontSize: `${pos.fontSize}px`,
                  fontWeight: pos.fontWeight,
                  color: pos.color,
                  textAlign: pos.textAlign,
                  maxWidth: pos.maxWidth ? `${pos.maxWidth}%` : undefined,
                }}
                onMouseDown={(e) => handleMouseDown(key, e)}
              >
                {fv.labelPrefix && fv.value && (
                  <div className={styles.fieldLabel}>
                    {fv.labelPrefix.toLocaleUpperCase("tr-TR")}
                  </div>
                )}
                <div className={styles.fieldValue}>{display}</div>
              </div>
            );
          })}
        </div>
        <p className={styles.hint}>
          Bir alanı seçmek için üzerine tıklayın. Konumlandırmak için sürükleyin.
        </p>
      </div>

      <aside className={styles.sidebar}>
        <div className={styles.sidebarHead}>
          <h4>{t("certificatePositionEditor.fieldSettings")}</h4>
          <button type="button" className={styles.resetBtn} onClick={onReset}>
            Varsayılana Dön
          </button>
        </div>

        <div className={styles.fieldTabs}>
          {FIELD_LIST.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`${styles.fieldTab} ${
                selected === f.key ? styles.fieldTabActive : ""
              }`}
              onClick={() => setSelected(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className={styles.controls}>
          <div className={styles.row}>
            <label>X (%)</label>
            <input
              type="number"
              min={0}
              max={100}
              step={0.1}
              value={current.x}
              onChange={(e) =>
                updatePos(selected, { x: Number(e.target.value) })
              }
            />
          </div>
          <div className={styles.row}>
            <label>Y (%)</label>
            <input
              type="number"
              min={0}
              max={100}
              step={0.1}
              value={current.y}
              onChange={(e) =>
                updatePos(selected, { y: Number(e.target.value) })
              }
            />
          </div>
          <div className={styles.row}>
            <label>{t("certificatePositionEditor.fontSize")}</label>
            <input
              type="number"
              min={8}
              max={96}
              step={1}
              value={current.fontSize}
              onChange={(e) =>
                updatePos(selected, { fontSize: Number(e.target.value) })
              }
            />
          </div>
          <div className={styles.row}>
            <label>Font</label>
            <select
              value={current.fontFamily}
              onChange={(e) =>
                updatePos(selected, { fontFamily: e.target.value })
              }
            >
              {FONT_FAMILIES.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.row}>
            <label>{t("certificatePositionEditor.fontWeight")}</label>
            <select
              value={current.fontWeight}
              onChange={(e) =>
                updatePos(selected, { fontWeight: e.target.value })
              }
            >
              {FONT_WEIGHTS.map((w) => (
                <option key={w} value={w}>
                  {w}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.row}>
            <label>Renk</label>
            <input
              type="color"
              value={current.color}
              onChange={(e) =>
                updatePos(selected, { color: e.target.value })
              }
            />
          </div>
          <div className={styles.row}>
            <label>Hizalama</label>
            <div className={styles.alignRow}>
              {(["left", "center", "right"] as const).map((a) => (
                <button
                  key={a}
                  type="button"
                  className={`${styles.alignBtn} ${
                    current.textAlign === a ? styles.alignBtnActive : ""
                  }`}
                  onClick={() => updatePos(selected, { textAlign: a })}
                >
                  {a === "left" ? t("certificatePositionEditor.alignment.left") : a === "center" ? t("certificatePositionEditor.alignment.center") : t("certificatePositionEditor.alignment.right")}
                </button>
              ))}
            </div>
          </div>
          <div className={styles.row}>
            <label>{t("certificatePositionEditor.maxWidth")}</label>
            <input
              type="number"
              min={10}
              max={100}
              step={1}
              value={current.maxWidth ?? 100}
              onChange={(e) =>
                updatePos(selected, { maxWidth: Number(e.target.value) })
              }
            />
          </div>
        </div>
      </aside>
    </div>
  );
};

export default CertificatePositionEditor;
